import { useState } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';

const FIELD_LABELS = {
  name: 'Name',
  vendor: 'Vendor',
  examnumber: 'Exam #',
  expected_exam_questions: 'Expected Exam Questions',
  official_url: 'Official URL',
  lab_guide_viable: 'Lab Guide Viable',
};

const inputStyle = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  borderRadius: '6px',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '0.875rem',
  boxSizing: 'border-box',
};
const labelStyle = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  marginBottom: '0.25rem',
  display: 'block',
};
const fieldStyle = { marginBottom: '0.85rem' };
const readValueStyle = {
  fontSize: '0.875rem',
  color: 'var(--color-text)',
  padding: '0.4rem 0.6rem',
  borderRadius: '6px',
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  minHeight: '2rem',
  display: 'flex',
  alignItems: 'center',
};

export default function NewTopicModal({ topic, isAdmin, onClose }) {
  const { authFetch } = useAuthFetch();
  const [form, setForm] = useState({
    name: topic.name ?? '',
    vendor: topic.vendor ?? '',
    examnumber: topic.examnumber ?? '',
    expected_exam_questions: topic.expected_exam_questions ?? 0,
    official_url: topic.official_url ?? '',
    lab_guide_viable: topic.lab_guide_viable ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        vendor: form.vendor,
        examnumber: form.examnumber,
        expected_exam_questions: parseInt(form.expected_exam_questions, 10) || 0,
        official_url: form.official_url || null,
        lab_guide_viable: form.lab_guide_viable,
      };
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/topics/${topic.id}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Server error ${res.status}`);
      }
      const updated = await res.json();
      onClose(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(null); };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        background: 'var(--color-bg)', borderRadius: '12px', border: '1px solid var(--color-border)',
        padding: '1.5rem', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Topic Created</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', marginTop: '2px' }}>
              {isAdmin ? 'Review and edit the AI-populated fields below.' : 'AI populated the following details.'}
            </div>
          </div>
          <button
            onClick={() => onClose(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)', lineHeight: 1 }}
          >✕</button>
        </div>

        {isAdmin ? (
          <>
            <div style={fieldStyle}>
              <label style={labelStyle}>{FIELD_LABELS.name}</label>
              <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>{FIELD_LABELS.vendor}</label>
              <input style={inputStyle} value={form.vendor} onChange={e => set('vendor', e.target.value)} placeholder="e.g. Microsoft" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>{FIELD_LABELS.examnumber}</label>
              <input style={inputStyle} value={form.examnumber} onChange={e => set('examnumber', e.target.value)} placeholder="e.g. AZ-104" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>{FIELD_LABELS.expected_exam_questions}</label>
              <input style={inputStyle} type="number" min="0" value={form.expected_exam_questions} onChange={e => set('expected_exam_questions', e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>{FIELD_LABELS.official_url}</label>
              <input style={inputStyle} value={form.official_url} onChange={e => set('official_url', e.target.value)} placeholder="https://…" />
            </div>
            <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input
                type="checkbox"
                id="new_topic_lab_guide_viable"
                checked={form.lab_guide_viable}
                onChange={e => set('lab_guide_viable', e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="new_topic_lab_guide_viable" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                {FIELD_LABELS.lab_guide_viable}
              </label>
            </div>
            {error && <p style={{ color: 'var(--color-error, #ef4444)', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button onClick={() => onClose(null)} className="costs-range-btn" disabled={saving}>Skip</button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)',
                  background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem',
                  fontWeight: 500, cursor: saving ? 'wait' : 'pointer',
                }}
              >
                {saving ? 'Saving…' : 'Save & Continue'}
              </button>
            </div>
          </>
        ) : (
          <>
            {[
              { label: FIELD_LABELS.name, value: form.name || '—' },
              { label: FIELD_LABELS.vendor, value: form.vendor || '—' },
              { label: FIELD_LABELS.examnumber, value: form.examnumber || '—' },
              { label: FIELD_LABELS.expected_exam_questions, value: form.expected_exam_questions || '—' },
              {
                label: FIELD_LABELS.official_url,
                value: form.official_url
                  ? <a href={form.official_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)' }}>{form.official_url}</a>
                  : '—',
              },
              { label: FIELD_LABELS.lab_guide_viable, value: form.lab_guide_viable ? 'Yes' : 'No' },
            ].map(({ label, value }) => (
              <div key={label} style={fieldStyle}>
                <label style={labelStyle}>{label}</label>
                <div style={readValueStyle}>{value}</div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button
                onClick={() => onClose(null)}
                style={{
                  padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)',
                  background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem',
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                Continue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
